<template>
  <panel-layout
    id="reservations-panel"
    :loading="!!(reservations && reservations.loading) || loading || false"
    :heading="$t('general.appointments')"
    :show-footer="false"
  >
    <template #heading>
      <div class="flex items-center space-x-3">
        <h2 class="focus:outline-none text-xl tracking-tight font-semibold text-gray-800 items-center dark:text-gray-100">
          {{ $t('general.appointments') }}
        </h2>
      </div>
    </template>

    <template #actions-left>
      <o-button
        variant="primary"
        icon="customerAdd"
        @click="handleAddCustomer"
      >
        {{ $t('customer.add') }}
      </o-button>
    </template>

    <template #actions-right>
      <o-tooltip :text="$t('pages.appointment.list.export')">
        <o-button icon="export" flat @click="handleExport" />
      </o-tooltip>
    </template>

    <div class="h-full min-h-0 flex flex-col overflow-hidden text-sm text-gray-800">
      <o-tabs
        class="min-h-0"
        container-class="min-h-0"
      >
        <o-tab v-if="reservationIDs.length" :name="$t('general.appointments')">
          <o-table
            v-model:data="reservations"
            :headers="[
              ' ',
              $t('general.customer'),
              $t('general.dateTime'),
              $t('general.services'),
              $t('general.employees'),
              $t('general.duration'),
              $t('general.price'),
              $t('general.status'),
              $t('general.actions')
            ]"
            scroll
            class="flex-shrink flex-grow min-h-0"
          >
            <template #row="props">
              <td :class="props.tdClass">
                <div
                  class="flex flex-col self-stretch mr-4 rounded overflow-hidden divide-y-1 divide-white h-6 w-[4px]"
                >
                  <div
                    v-for="service in props.row.services"
                    :key="`${props.row.idReservation}-${service.idReservationService}`"
                    class="w-full flex-grow"
                    :style="{
                      backgroundColor: '#' + service.service.borderHexColour
                    }"
                  />
                </div>
              </td>
              <td :class="props.tdClass">
                {{ props.row.customer ? props.row.customer.displayName : '-' }}
              </td>
              <td :class="props.tdClass">
                {{ props.row.dateLabel }}
              </td>
              <td :class="props.tdClass">
                {{ props.row._servicesLabel || '-' }}
              </td>
              <td :class="props.tdClass">
                {{ props.row.employeesLabel || '-' }}
              </td>
              <td :class="props.tdClass">
                {{ props.row.durationLabel || '-' }}
              </td>
              <td :class="props.tdClass">
                {{ props.row.priceLabel || '-' }}
              </td>
              <td :class="props.tdClass">
                <status :reservation="props.row" size="xs" compact class="flex-wrap" />
              </td>
              <td :class="props.tdClass">
                <o-panel-link :path="`/appointment/${props.row.idReservation}?date=${props.row.date}`" slim flat icon="view" tag="o-button" />
              </td>
            </template>
          </o-table>
        </o-tab>

        <o-tab v-if="reservationCustomerIDs.length" :name="$t('pages.appointment.list.attendees')">
          <o-table
            v-model:data="reservationCustomerPager"
            :headers="[
              ' ',
              $t('general.customer'),
              $t('general.dateTime'),
              $t('general.price'),
              $t('general.actions'),
            ]"
            scroll
            class="flex-shrink flex-grow min-h-0"
          >
            <template #row="props">
              <td :class="props.tdClass">
                <div
                  class="flex flex-col self-stretch mr-4 rounded overflow-hidden divide-y-1 divide-white h-6 w-[4px]"
                >
                  <!--<div
                    v-for="service in props.row.services"
                    :key="`${props.row.idReservation}-${service.idReservationService}`"
                    class="w-full flex-grow"
                    :style="{
                      backgroundColor: '#' + service.service.borderHexColour
                    }"
                  />-->
                </div>
              </td>
              <td :class="props.tdClass">
                {{ props.row.customer ? props.row.customer.displayName : '-' }}
              </td>
              <td :class="props.tdClass">
                {{ $date.convert(props.row.date, undefined, $date.presets.short) }}
              </td>
              <td :class="props.tdClass">
                {{ props.row.priceLabel || '-' }}
              </td>
              <!--
              <td :class="props.tdClass">
                {{ props.row._servicesLabel || '-' }}
              </td>
              <td :class="props.tdClass">
                {{ props.row.employeesLabel || '-' }}
              </td>
              <td :class="props.tdClass">
                {{ props.row.durationLabel || '-' }}
              </td>
              <td :class="props.tdClass">
                <status :reservation="props.row" size="xs" compact class="flex-wrap" />
              </td>-->
              <td :class="props.tdClass">
                <o-panel-link :path="`/appointment/attendee/${props.row.idReservationCustomer}?date=${props.row.date}`" slim flat icon="customer" tag="o-button" />
                <o-panel-link :path="`/appointment/${props.row.reservation.idReservation}?date=${props.row.reservation.date}`" slim flat icon="reservationClass" tag="o-button" />
              </td>
            </template>
          </o-table>
        </o-tab>
      </o-tabs>
    </div>
  </panel-layout>
</template>
<script setup lang="ts">
import { utils, writeFileXLSX } from 'xlsx'
import { ReservationStatus, ReservationStatusLabels } from '~/_api/models/reservation'

// @ts-ignore
import Status from '@/components/reservation/elements/Status.vue'
// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'

// @ts-ignore
import {
  OTable,
  OTabs,
  OTab,
} from '@/components/elements'

import {
  TablePager,
} from '@/_api/lib/pager'

import { usePanel } from '~/composables/usePanel'

const panel = usePanel()

const props = defineProps<{
  ids?: string
  reservationCustomers?: string
}>()

const { $api, $date, $notification, $i18n, $panel } = useNuxtApp()

const reservationIDs = computed(() => props.ids?.split(',') || [])
const reservationCustomerIDs = computed(() => props.reservationCustomers?.split(',') || [])

const reservations = ref<any>(new TablePager(page => $api.reservation.ids(reservationIDs.value, page), 20))
const reservationCustomerPager = ref<any>(new TablePager(page => $api.reservationCustomer.ids(reservationCustomerIDs.value, page), 20))
const loading = ref(false)

const fetchReservations = async () => {
  try {
    if (reservationIDs.value.length) {
      await reservations.value.load()
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }
}

const fetchReservationCustomers = async () => {
  try {
    if (reservationCustomerIDs.value.length) {
      await reservationCustomerPager.value.load()
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }
}

const handleExport = async () => {
  loading.value = true

  try {
    const reservationIDs = props.ids?.split(',') || []
    const reservationCustomerIDs = props.reservationCustomers?.split(',') || []
    let data: Record<string, any>[] = []
    let sheetName = 'Reservations'
    let fileName = 'reservations.xlsx'

    if (reservationIDs.length) {
      const reservationsList = await $api.reservation.listIDs(reservationIDs)
      data = reservationsList
        .map((reservation: any) => {
          return {
            info: reservation.infoLabel,
            idReservation: reservation.idReservation,
            idCustomer: reservation.customer?.idCustomer,
            customer: reservation.customer?.displayName,
            date: reservation.dateLabel,
            status: reservation.status != null ? $i18n.t(ReservationStatusLabels[reservation.status as ReservationStatus]) : '',
            services: reservation.services?.map((service: any) => service.service?.title).join(', '),
            employees: reservation.employeesLabel,
            duration: reservation.durationLabel,
            price: reservation.priceLabel,
            notes: reservation.notes,
          }
        })
    } else if (reservationCustomerIDs.length) {
      const reservationCustomersList = await $api.reservationCustomer.listIDs(reservationCustomerIDs)
      data = reservationCustomersList
        .map((reservationCustomer: any) => {
          return {
            info: reservationCustomer.infoLabel || reservationCustomer.reservation?.infoLabel,
            idReservationCustomer: reservationCustomer.idReservationCustomer,
            idReservation: reservationCustomer.reservation?.idReservation,
            idCustomer: reservationCustomer.customer?.idCustomer,
            customer: reservationCustomer.customer?.displayName,
            date: reservationCustomer.date ? $date.convert(reservationCustomer.date, undefined, $date.presets.short) : '',
            status: reservationCustomer.status != null ? $i18n.t(ReservationStatusLabels[reservationCustomer.status as ReservationStatus]) : '',
            service: reservationCustomer.reservation?.service?.title,
            employee: reservationCustomer.reservation?.employee?.displayName,
            price: reservationCustomer.priceLabel,
            notes: reservationCustomer.notes,
          }
        })
      sheetName = 'Attendees'
      fileName = 'reservation-attendees.xlsx'
    }

    if (data.length) {
      const workbook = utils.book_new()
      const worksheet = utils.json_to_sheet(data)

      const widths = Object.keys(data[0])
        .map((key: string) => {
          return {
            wch: Math.max(...data
              .map((item: any) => {
                return item[key]?.length || 30
              }),
            ),
          }
        })

      worksheet['!cols'] = widths
      utils.book_append_sheet(workbook, worksheet, sheetName)

      writeFileXLSX(workbook, fileName)
    }
  } catch (e) {
    console.log(e)
  }

  loading.value = false
}

const handleAddCustomer = () => {
  $panel.create('/customer/edit')
}

onMounted(() => {
  fetchReservations()
  fetchReservationCustomers()
})
</script>
